import React, { FC, useState, useEffect } from 'react';
import LocalModal from 'components/base/local-modal';
import { ReactComponent as ListIcon } from 'assets/icons/list-icon.svg';
import { Button } from '@cokitchen/cokitchen-components';
import CsvIcon from 'assets/images/csv.png';
import CsvInfoModal from '../../../../components/modals/csv-info-modal';
import { useNavigate } from 'react-router-dom';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import useVendorContext from 'contexts/vendor.context';
import { DefaultvendorFormData } from '../add-vendor';
import * as XLSX from 'xlsx';
import { VendorFormType } from 'models/vendors.model';
import useDashboardContext from 'contexts/dashboard.context';
import { appendZeroToPhoneNumber, convertKeysToSnakeCase } from 'utils/functions';

type AddVendorProps = {
  show: boolean;
  onClose: () => void;
};

const VendorAdditionDetails = {
  subtitle:
    'Welcome to the Vendor addition process via CSV file. Please Follow these steps to ensure a smooth member addition:',
  steps: [
    {
      number: 1,
      heading: 'Download the Template',
      description: "Click the 'Download CSV Template' button to obtain the template file."
    },
    {
      number: 2,
      heading: "Fill in vendors' Details",
      description: ' Open the downloaded CSV file, Specify the role of each vendor.'
    },
    {
      number: 3,
      heading: 'Save Your Changes',
      description: 'Save the CSV file with your entries'
    },
    {
      number: 4,
      heading: 'Review',
      description:
        'Crosscheck the details in the CSV file. Click confirm to select the file and upload'
    },
    {
      number: 5,
      heading: 'Verify the details',
      description:
        'After uploading, the details will be displayed for final review before addition,then click continue to add members to the company'
    }
  ]
};
const AddVendorModal: FC<AddVendorProps> = ({ show, onClose }) => {
  const { formStoreItems } = useDashboardContext();
  const navigate = useNavigate();
  const [showCsvInfoModal, setShowCsvInfoModal] = useState(false);
  const { active, setActive, setVendorFormData, setStep } = useVendorContext();
  const [file, setFile] = useState<File | null>(null);
  const toggleCsvInfoModal = () => {
    setShowCsvInfoModal(!showCsvInfoModal);
  };

  const readFile = () => {
    if (file) {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = (event) => {
        const arrayBuffer = event.target?.result;
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const vendorSheet = workbook.Sheets[workbook.SheetNames[0]];
        const storeItemSheet = workbook.Sheets[workbook.SheetNames[1]];

        let vendorData: VendorFormType[] = convertKeysToSnakeCase(
          XLSX.utils.sheet_to_json(vendorSheet)
        );
        vendorData = vendorData.map((vendor) => ({
          ...vendor,
          phone_number: appendZeroToPhoneNumber(String(vendor.phone_number)),
          payment_period:
            Number(vendor.payment_period) === 0 || Number(vendor.payment_period) === 1
              ? '1 day'
              : `${vendor.payment_period} days`
        }));

        const storeItemData: { vendor_name: string; store_item: string; price: number }[] =
          convertKeysToSnakeCase(XLSX.utils.sheet_to_json(storeItemSheet));

        vendorData.forEach((vendor) => {
          if (!vendor.store_items) {
            vendor.store_items = [];
          }
        });

        const storeItemMap = formStoreItems.reduce((acc: any, item: any) => {
          acc[item.name] = item.id;
          return acc;
        }, {});

        storeItemData.forEach((item) => {
          if (item.vendor_name) {
            const vendor = vendorData.find(
              (v) => v.name.toLowerCase() === item.vendor_name.toLowerCase()
            );
            if (vendor) {
              const ingredientId = storeItemMap[item.store_item];
              if (ingredientId) {
                vendor.store_items.push({
                  ingredient_id: ingredientId,
                  gram_price: item.price.toString()
                });
              }
            }
          }
        });

        setVendorFormData(vendorData);
        navigate(MAIN_STORE_ROUTE_KEYS.ADD_VENDOR);
        setStep(3);
        setActive(null);
      };
    }
  };

  useEffect(() => {
    readFile();
  }, [file]);

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    setActive('csv');
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files) setFile(files[0]);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };
  const handleFileUpload = (e: any) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
    }
  };
  const reset = () => {
    setActive('csv');
    setVendorFormData([]);
  };

  return (
    <LocalModal
      isOpen={show}
      onRequestClose={() => {
        onClose();
        setActive(null);
      }}
      title='Add new vendor'
      contentClassName='!max-w-[442px]'>
      <div className='flex gap-4 my-4 w-full '>
        <div
          onClick={() => {
            setActive('csv');
            toggleCsvInfoModal();
          }}
          onDrop={handleFileDrop}
          onDragOver={handleDragOver}
          className={`${
            active === 'csv' ? 'border-[#E60A2B] border-[1.5px]' : 'border-[#EAEBEB]'
          } border w-full sm:w-[185px] h-[139px]  hover:border-[#E60A2B] cursor-pointer hover:border-[1.5px] p-4 rounded-md space-y-3`}>
          <div className='border border-[#EAEBEB] p-2 rounded-full w-9 h-9 flex items-center'>
            <img src={CsvIcon} alt='' />
          </div>
          <h2 className='text-sm font-semibold'>Add via .csv</h2>
          <p className='text-[#6F7376] text-xs font-normal'>
            Add multiple vendors to the vendor list
          </p>
        </div>

        <div
          onClick={() => {
            setActive('manual');
            setVendorFormData([DefaultvendorFormData]);
          }}
          className={`${
            active === 'manual' ? 'border-[#E60A2B] border-[1.5px]' : 'border-[#EAEBEB] '
          } border w-full sm:w-[185px] h-[139px] hover:border-[#E60A2B] cursor-pointer hover:border-[1.5px] p-4 rounded-md space-y-3`}>
          <div className='border border-[#EAEBEB] p-2 rounded-full w-9 h-9 flex items-center'>
            <ListIcon />
          </div>
          <h2 className='text-sm font-semibold'>Add Manually</h2>
          <p className='text-[#6F7376] text-xs font-normal '>Add multiple vendors manually</p>
        </div>
      </div>
      <div className='flex justify-end mt-6 '>
        <Button
          color='transparent'
          variant='block'
          small
          className='w-[139px] '
          onClick={() => {
            onClose();
            setActive(null);
          }}>
          Cancel
        </Button>
        <Button
          color='primary'
          variant='block'
          small
          className='w-[139px]'
          onClick={() => navigate(MAIN_STORE_ROUTE_KEYS.ADD_VENDOR)}>
          Confirm
        </Button>
      </div>
      {showCsvInfoModal && (
        <CsvInfoModal
          show={showCsvInfoModal}
          onClose={toggleCsvInfoModal}
          handleFileUpload={handleFileUpload}
          handleFileChange={reset}
          title='Add Vendors via CSV file'
          downloadLink='https://docs.google.com/spreadsheets/d/1Ga-jSfDgFap0to7qjeKoFPUfmWl1udbNRnYFLcm4gFs/edit?gid=0#gid=0'>
          <div className='w-full max-w-[400px]'>
            <h2 className='text-sm text-[#2E3031]'>{VendorAdditionDetails.subtitle}</h2>
            <div>
              {VendorAdditionDetails.steps.map((step) => (
                <div className='flex items-center max-w-[400px]' key={step.number}>
                  <div className='inline-block relative'>
                    <span className='w-5 h-5 bg-[#e7e8e9] text-sm rounded-full flex items-center justify-center mb-2 mr-2 p-1'>
                      {step.number}
                    </span>
                  </div>
                  <span className='mt-3'>
                    <h3 className='text-sm font-medium'>{step.heading}</h3>
                    <p className='text-xs'>{step.description}</p>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </CsvInfoModal>
      )}
    </LocalModal>
  );
};

export default AddVendorModal;
